import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { GoogleIcon, KakaoIcon } from '../../components/icons/SNSIcons';
import { useAuth } from '../../contexts/AuthContext';
import { createUserProfile } from '../../services/supabaseService';
import { getAuthErrorMessage, logError, ErrorInfo } from '../../utils/errorHandling';

const LoginPage: React.FC = () => {
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [loading, setLoading] = useState(false);
  
  const { loginWithGoogle, loginWithKakao } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // location.state에서 에러 정보 확인
    const state = location.state as { error?: string; action?: string } | null;
    if (state?.error) {
      setErrorInfo({
        message: state.error,
        type: 'error',
        action: state.action
      });
      // state 초기화
      navigate(location.pathname, { replace: true });
    }

    // 카카오 로그인 진행 중인지 확인
    const isKakaoLoginInProgress = sessionStorage.getItem('kakao_login_in_progress');
    if (isKakaoLoginInProgress) {
      setLoading(true);
      // 5초 후에도 진행 상태가 남아있으면 정리
      const timeout = setTimeout(() => {
        sessionStorage.removeItem('kakao_login_in_progress');
        setLoading(false);
      }, 5000);
      
      return () => clearTimeout(timeout);
    }
  }, [location, navigate]);

  const handleSocialLogin = async (provider: 'google' | 'kakao') => {
    try {
      setErrorInfo(null);
      setLoading(true);
      
      if (provider === 'google') {
        const result = await loginWithGoogle();
        if (result.user) {
          await createUserProfile(result.user.uid, {
            email: result.user.email,
            displayName: result.user.displayName,
            photoURL: result.user.photoURL,
            provider: 'google'
          });
          navigate('/mypage');
        }
      } else {
        // 카카오 로그인의 경우 리다이렉트되므로 로딩 상태를 유지
        // 중복 클릭 방지를 위해 버튼을 비활성화
        loginWithKakao();
        // 카카오 로그인은 리다이렉트되므로 여기서 로딩 상태를 해제하지 않음
        return; // 함수 종료
      }
    } catch (error: any) {
      logError(`${provider}Login`, error);
      const errorInfo = getAuthErrorMessage(error.code) || {
        message: `${provider === 'google' ? '구글' : '카카오'} 로그인 중 오류가 발생했습니다.`,
        type: 'error',
        action: '다시 시도해주세요.'
      };
      setErrorInfo(errorInfo);
    } finally {
      // 카카오 로그인이 아닌 경우에만 로딩 상태 해제
      if (provider !== 'kakao') {
        setLoading(false);
      }
    }
  };

  const commonButtonStyle = {
    height: '48px',
    fontWeight: 500,
    fontSize: '16px',
    '&:disabled': {
      opacity: 0.6
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" align="center" gutterBottom>
          로그인
        </Typography>

        {errorInfo && (
          <Alert 
            severity={errorInfo.type} 
            sx={{ mb: 3 }}
          >
            <Typography variant="body2" sx={{ mb: errorInfo.action ? 1 : 0 }}>
              {errorInfo.message}
            </Typography>
            {errorInfo.action && (
              <Typography variant="body2" color="text.secondary">
                {errorInfo.action}
              </Typography>
            )}
          </Alert>
        )}

        <Box sx={{ mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<GoogleIcon size={20} />}
            onClick={() => handleSocialLogin('google')}
            disabled={loading}
            sx={{ 
              ...commonButtonStyle,
              mb: 2,
              backgroundColor: '#ffffff',
              color: '#000000',
              border: '1px solid #dadce0',
              '&:hover': {
                backgroundColor: '#f8f9fa',
                border: '1px solid #dadce0',
              }
            }}
          >
            Google로 계속하기
          </Button>
          
          <Button
            fullWidth
            variant="contained"
            startIcon={<KakaoIcon size={20} />}
            onClick={() => handleSocialLogin('kakao')}
            disabled={loading}
            sx={{ 
              ...commonButtonStyle,
              backgroundColor: '#FEE500',
              color: '#000000',
              '&:hover': {
                backgroundColor: '#FEE500',
                opacity: 0.9
              }
            }}
          >
            카카오로 계속하기
          </Button>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2} gap={1}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              로그인 처리 중...
            </Typography>
          </Box>
        )}
        
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            로그인 시 서비스 이용약관과 개인정보 처리방침에 동의하는 것으로 간주됩니다.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage; 